import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMeeting, API } from "../App";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Zap, ArrowLeft, ArrowRight, Info, CheckCircle2, ImageIcon, X, Settings } from "lucide-react";
import ImageLibraryManager from "../components/ImageLibraryManager";
import AssetConfigManager from "../components/AssetConfigManager";
import TransformerSchematic from "../components/TransformerSchematic";
import { usePresentationMode } from "../components/Layout";
import InteractiveColumns from "../components/InteractiveColumns";
import { TRANSFORMER_DATA, getSimpleTransformerComponents } from "../data/transformerData";
import axios from "axios";

// Default data - will be overridden by API config if available
const DEFAULT_COMPONENTS = TRANSFORMER_DATA;

const TransformerPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const { currentMeeting, toggleAsset, isAssetSelected } = useMeeting();
  const { isPresentationMode, enterPresentationMode } = usePresentationMode();
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [isImageLibraryOpen, setIsImageLibraryOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [customImage, setCustomImage] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [components, setComponents] = useState(DEFAULT_COMPONENTS);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // Auto-enter presentation mode if query param is present
  useEffect(() => {
    if (searchParams.get('presentation') === 'true' && !isPresentationMode) {
      enterPresentationMode();
      // Remove the query param after entering presentation mode
      searchParams.delete('presentation');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, isPresentationMode, enterPresentationMode, setSearchParams]);

  useEffect(() => {
    fetchActiveImage();
    fetchConfig();
  }, []);

  // Utility function to merge images from default data into custom config
  const mergeImagesFromDefault = (customComponents, defaultComponents) => {
    return customComponents.map(customComp => {
      const defaultComp = defaultComponents.find(d => d.id === customComp.id || d.name === customComp.name);
      if (!defaultComp) return customComp;

      // Merge images for failure modes
      const mergedFailureModes = (customComp.failureModes || []).map(customFM => {
        const defaultFM = (defaultComp.failureModes || []).find(d => d.name === customFM.name);
        if (defaultFM && defaultFM.images && !customFM.images) {
          return { ...customFM, images: defaultFM.images };
        }
        return customFM;
      });

      // Merge images for tests
      const mergedTests = (customComp.tests || []).map(customTest => {
        const defaultTest = (defaultComp.tests || []).find(d => d.name === customTest.name);
        if (defaultTest && defaultTest.images && !customTest.images) {
          return { ...customTest, images: defaultTest.images };
        }
        return customTest;
      });

      // Merge images for monitoring
      const mergedMonitoring = (customComp.monitoring || []).map(customMon => {
        const defaultMon = (defaultComp.monitoring || []).find(d => d.name === customMon.name);
        if (defaultMon && defaultMon.images && !customMon.images) {
          return { ...customMon, images: defaultMon.images };
        }
        return customMon;
      });

      return {
        ...customComp,
        failureModes: mergedFailureModes,
        tests: mergedTests,
        monitoring: mergedMonitoring
      };
    });
  };

  const fetchConfig = async () => {
    try {
      setIsLoadingConfig(true);
      // Priority 1: Check asset-configs (user customizations with images)
      try {
        const configResp = await axios.get(`${API}/asset-configs/transformers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (configResp.data && configResp.data.components && configResp.data.components.length > 0) {
          const mergedComponents = mergeImagesFromDefault(configResp.data.components, DEFAULT_COMPONENTS);
          setComponents(mergedComponents);
          return;
        }
      } catch (e) { /* no custom config */ }
      // Priority 2: Check asset-types (admin-seeded dynamic config)
      try {
        const response = await axios.get(`${API}/asset-types/transformers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.components && response.data.components.length > 0) {
          const mergedComponents = mergeImagesFromDefault(response.data.components, DEFAULT_COMPONENTS);
          setComponents(mergedComponents);
          return;
        }
      } catch (e) { /* no dynamic config */ }
    } catch (error) {
      console.error("Error loading configuration");
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const handleConfigSaved = (config) => {
    if (config && config.components && config.components.length > 0) {
      // Merge images from default data into saved config
      const mergedComponents = mergeImagesFromDefault(config.components, DEFAULT_COMPONENTS);
      setComponents(mergedComponents);
    }
  };

  const fetchActiveImage = async () => {
    try {
      setIsLoadingImage(true);
      const response = await axios.get(`${API}/asset-images/transformers/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCustomImage(response.data);
      }
    } catch (error) {
      console.error("No active image found, using default");
    } finally {
      setIsLoadingImage(false);
    }
  };

  if (!currentMeeting) {
    navigate("/landing");
    return null;
  }

  const handleComponentClick = (component) => {
    const fullComponent = components.find(c => c.id === component.id || c.id === String(component.number));
    setSelectedComponent(selectedComponent?.id === (component.id || String(component.number)) ? null : fullComponent);
  };

  const handleMarkerClick = (componentId) => {
    const fullComponent = components.find(c => c.id === componentId);
    setSelectedComponent(selectedComponent?.id === componentId ? null : fullComponent);
  };

  const handleSelectItem = (type, itemName, componentId) => {
    toggleAsset(type, `${componentId}-${itemName}`, "transformers");
  };

  const isItemSelected = (type, itemName, componentId) => {
    return isAssetSelected(type, `${componentId}-${itemName}`, "transformers");
  };

  const handleImageSaved = (imageData) => {
    setCustomImage(imageData);
  };

  const closeDetails = () => {
    setSelectedComponent(null);
  };

  // Presentation Mode with Detail View
  if (isPresentationMode && selectedComponent) {
    return (
      <div data-testid="transformer-page" className="h-full">
        <div className="grid grid-cols-12 gap-6">
          {/* Left - Compact Transformer Diagram - FIXED HEIGHT */}
          <div className="col-span-4">
            <div 
              className="bg-gradient-to-b from-sky-50 to-slate-100 rounded-lg border border-slate-200 p-3 flex flex-col"
              style={{ height: 'calc(100vh - 120px)' }}
            >
              {/* Image container - takes remaining space */}
              <div className="flex-1 relative flex items-center justify-center overflow-hidden">
                {isLoadingImage ? (
                  <div className="flex flex-col items-center justify-center text-slate-400">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                    <p className="text-sm">Loading image...</p>
                  </div>
                ) : customImage ? (
                  <div className="relative inline-block">
                    <img 
                      src={customImage.image_url} 
                      alt={customImage.image_name}
                      className="max-w-full max-h-full rounded object-contain"
                    />
                    {customImage.markers?.map((marker) => {
                      const component = components.find(c => c.id === marker.component_id);
                      if (!component) return null;
                      return (
                        <button
                          key={marker.component_id}
                          onClick={() => handleMarkerClick(marker.component_id)}
                          className={`absolute w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm transition-all ${
                            selectedComponent?.id === marker.component_id 
                              ? 'scale-125 ring-2 ring-white shadow-xl' 
                              : 'hover:scale-110 shadow-md'
                          }`}
                          style={{
                            left: `${marker.x}%`,
                            top: `${marker.y}%`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: component.color,
                            border: "2px solid white"
                          }}
                        >
                          {component.number}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-slate-400">
                    <p className="text-sm">No image configured</p>
                    <p className="text-xs mt-1">Use "Images" to upload a transformer image</p>
                  </div>
                )}
              </div>
              
              {/* Component Quick Nav - Always visible at bottom */}
              <div className="flex justify-center gap-2 pt-3 mt-2 border-t border-slate-200 flex-shrink-0">
                {components.map((comp) => (
                  <button
                    key={comp.id}
                    onClick={() => handleComponentClick(comp)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold transition-all ${
                      selectedComponent?.id === comp.id ? 'scale-110 ring-2 ring-offset-2' : 'opacity-70 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: comp.color, '--tw-ring-color': comp.color }}
                    title={comp.name}
                  >
                    {comp.number}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Component Details - Independent scrolling */}
          <div className="col-span-8 flex flex-col" style={{ height: 'calc(100vh - 120px)' }}>
            {/* Component Header - Fixed */}
            <div 
              className="flex items-center justify-between mb-3 pb-3 border-b-2 flex-shrink-0"
              style={{ borderColor: selectedComponent.color }}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                  style={{ backgroundColor: selectedComponent.color }}
                >
                  {selectedComponent.number}
                </div>
                <h2 className="text-xl font-semibold text-heading text-slate-900">
                  {selectedComponent.name}
                </h2>
              </div>
              <button
                onClick={closeDetails}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                title="Back to overview"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Three Columns - Scrollable container */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <InteractiveColumns
                component={selectedComponent}
                onSelectItem={handleSelectItem}
                isItemSelected={isItemSelected}
                compact={true}
              />
            </div>
          </div>
        </div>

        <ImageLibraryManager
          isOpen={isImageLibraryOpen}
          onClose={() => setIsImageLibraryOpen(false)}
          assetType="transformers"
          componentLabels={components.map(c => c.name)}
          onImageSaved={handleImageSaved}
        />
      </div>
    );
  }

  // Presentation Mode - Overview (no component selected)
  if (isPresentationMode) {
    return (
      <div data-testid="transformer-page">
        <div className="grid grid-cols-12 gap-4">
          {/* Left Panel - Component Labels */}
          <div className="col-span-3 space-y-2">
            {components.filter((_, i) => i < 3).map((component) => {
              const failureNames = component.failureModes.map(f => typeof f === 'string' ? f : f.name);
              return (
                <Card 
                  key={component.id}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md`}
                  onClick={() => handleComponentClick(component)}
                  data-testid={`component-${component.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div 
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: component.color }}
                      >
                        {component.number}
                      </div>
                      <h3 className="font-semibold text-slate-900 text-heading text-sm">{component.name}</h3>
                    </div>
                    <ul className="text-xs text-slate-500 space-y-0.5 ml-9">
                      {failureNames.slice(0, 2).map((mode, idx) => (
                        <li key={idx}>• {mode}</li>
                      ))}
                      <li className="text-blue-600">+ {failureNames.length - 2} more...</li>
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Center - Schematic */}
          <div className="col-span-6">
            <div className="relative bg-gradient-to-b from-sky-50 to-slate-100 rounded-lg border border-slate-200 p-3 flex items-center justify-center" style={{ minHeight: "420px" }}>
              {isLoadingImage ? (
                <div className="flex flex-col items-center justify-center text-slate-400">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
                  <p className="text-sm">Loading transformer image...</p>
                </div>
              ) : customImage ? (
                <div className="relative inline-block">
                  <img 
                    src={customImage.image_url} 
                    alt={customImage.image_name}
                    className="max-w-full rounded"
                    style={{ maxHeight: "380px" }}
                  />
                  {customImage.markers?.map((marker) => {
                    const component = components.find(c => c.id === marker.component_id);
                    if (!component) return null;
                    return (
                      <button
                        key={marker.component_id}
                        onClick={() => handleMarkerClick(marker.component_id)}
                        className="absolute w-9 h-9 rounded-full flex items-center justify-center text-white font-bold transition-all hover:scale-110 shadow-lg"
                        style={{
                          left: `${marker.x}%`,
                          top: `${marker.y}%`,
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: component.color,
                          border: "2px solid white"
                        }}
                      >
                        {component.number}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400">
                  <p className="text-sm">No transformer image configured</p>
                  <p className="text-xs mt-1">Use "Images" button to upload</p>
                </div>
              )}
              
              <div className="absolute bottom-2 left-2 right-2 bg-white/80 backdrop-blur-sm rounded px-3 py-2 text-center">
                <p className="text-xs text-slate-500">
                  Click on any component to view details
                </p>
              </div>
            </div>
          </div>

          {/* Right Panel - Component Labels */}
          <div className="col-span-3 space-y-2">
            {components.filter((_, i) => i >= 3).map((component) => {
              const failureNames = component.failureModes.map(f => typeof f === 'string' ? f : f.name);
              return (
                <Card 
                  key={component.id}
                  className={`cursor-pointer transition-all duration-200 hover:shadow-md`}
                  onClick={() => handleComponentClick(component)}
                  data-testid={`component-${component.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div 
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: component.color }}
                      >
                        {component.number}
                      </div>
                      <h3 className="font-semibold text-slate-900 text-heading text-sm">{component.name}</h3>
                    </div>
                    <ul className="text-xs text-slate-500 space-y-0.5 ml-9">
                      {failureNames.slice(0, 2).map((mode, idx) => (
                        <li key={idx}>• {mode}</li>
                      ))}
                      <li className="text-blue-600">+ {failureNames.length - 2} more...</li>
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <ImageLibraryManager
          isOpen={isImageLibraryOpen}
          onClose={() => setIsImageLibraryOpen(false)}
          assetType="transformers"
          componentLabels={components.map(c => c.name)}
          onImageSaved={handleImageSaved}
        />
      </div>
    );
  }

  // Normal Mode (with sidebar)
  return (
    <div data-testid="transformer-page" className="min-h-screen">
      {/* Header - Hidden in Presentation Mode */}
      {!isPresentationMode && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-50 rounded-md flex items-center justify-center">
              <Zap className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-heading text-slate-900">Transformer Condition Monitoring</h1>
              <p className="text-slate-500">Click on numbered components to explore failure modes and solutions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setIsConfigOpen(true)}
              data-testid="configure-btn"
              className="gap-2"
            >
              <Settings className="w-4 h-4" strokeWidth={1.5} />
              Configure
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsImageLibraryOpen(true)}
              data-testid="manage-images-btn"
              className="gap-2"
            >
              <ImageIcon className="w-4 h-4" strokeWidth={1.5} />
              Images
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard")}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Dashboard
            </Button>
            <Button
              onClick={() => navigate("/assets/switchgear")}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="next-asset"
            >
              Next: Switchgear
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      )}

      {/* Main Content - Visual Diagram Layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Panel - Component Labels */}
        <div className="col-span-3 space-y-3">
          {components.filter((_, i) => i < 3).map((component) => {
            const failureNames = component.failureModes.map(f => typeof f === 'string' ? f : f.name);
            return (
              <Card 
                key={component.id}
                className={`cursor-pointer transition-all duration-200 ${
                  selectedComponent?.id === component.id 
                    ? 'ring-2 shadow-lg' 
                    : 'hover:shadow-md'
                }`}
                style={{ 
                  borderColor: selectedComponent?.id === component.id ? component.color : undefined,
                  '--tw-ring-color': component.color 
                }}
                onClick={() => handleComponentClick(component)}
                data-testid={`component-${component.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: component.color }}
                    >
                      {component.number}
                    </div>
                    <h3 className="font-semibold text-slate-900 text-heading text-sm">{component.name}</h3>
                  </div>
                  <ul className="text-xs text-slate-500 space-y-1 ml-11">
                    {failureNames.slice(0, 3).map((mode, idx) => (
                      <li key={idx}>• {mode}</li>
                    ))}
                    {failureNames.length > 3 && (
                      <li className="text-blue-600">+ {failureNames.length - 3} more...</li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Center - Image/Schematic */}
        <div className="col-span-6">
          <div className="relative bg-gradient-to-b from-sky-50 to-slate-100 rounded-lg border border-slate-200 p-4 flex items-center justify-center" style={{ minHeight: "520px" }}>
            {isLoadingImage ? (
              <div className="flex flex-col items-center justify-center text-slate-400">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3"></div>
                <p className="text-sm">Loading transformer image...</p>
              </div>
            ) : customImage ? (
              <div className="relative inline-block">
                <img 
                  src={customImage.image_url} 
                  alt={customImage.image_name}
                  className="max-w-full rounded"
                  style={{ maxHeight: "480px" }}
                />
                {customImage.markers?.map((marker) => {
                  const component = components.find(c => c.id === marker.component_id);
                  if (!component) return null;
                  return (
                    <button
                      key={marker.component_id}
                      onClick={() => handleMarkerClick(marker.component_id)}
                      className={`absolute w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg transition-all duration-200 ${
                        selectedComponent?.id === marker.component_id 
                          ? 'scale-125 ring-4 ring-white shadow-xl' 
                          : 'hover:scale-110 shadow-lg'
                      }`}
                      style={{
                        left: `${marker.x}%`,
                        top: `${marker.y}%`,
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: component.color,
                        border: "3px solid white"
                      }}
                      data-testid={`marker-${marker.component_id}`}
                    >
                      {component.number}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-slate-400">
                <p className="text-sm">No transformer image configured</p>
                <p className="text-xs mt-1">Click "Images" to upload your transformer image</p>
              </div>
            )}
            
            <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-md p-3 border border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                <Info className="w-3 h-3 inline mr-1" />
                {customImage 
                  ? `Using: ${customImage.image_name} • Click "Images" to change`
                  : "Click \"Images\" to upload your transformer image with component markers"
                }
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Component Labels */}
        <div className="col-span-3 space-y-3">
          {components.filter((_, i) => i >= 3).map((component) => {
            const failureNames = component.failureModes.map(f => typeof f === 'string' ? f : f.name);
            return (
              <Card 
                key={component.id}
                className={`cursor-pointer transition-all duration-200 ${
                  selectedComponent?.id === component.id 
                    ? 'ring-2 shadow-lg' 
                    : 'hover:shadow-md'
                }`}
                style={{ 
                  borderColor: selectedComponent?.id === component.id ? component.color : undefined,
                  '--tw-ring-color': component.color 
                }}
                onClick={() => handleComponentClick(component)}
                data-testid={`component-${component.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: component.color }}
                    >
                      {component.number}
                    </div>
                    <h3 className="font-semibold text-slate-900 text-heading text-sm">{component.name}</h3>
                  </div>
                  <ul className="text-xs text-slate-500 space-y-1 ml-11">
                    {failureNames.slice(0, 3).map((mode, idx) => (
                      <li key={idx}>• {mode}</li>
                    ))}
                    {failureNames.length > 3 && (
                      <li className="text-blue-600">+ {failureNames.length - 3} more...</li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Selected Component Details Panel - Normal Mode */}
      {selectedComponent && (
        <div className="mt-6 animate-slide-up" data-testid="component-details">
          <Card className="border-2" style={{ borderColor: selectedComponent.color }}>
            <CardHeader className="pb-4" style={{ backgroundColor: `${selectedComponent.color}10` }}>
              <CardTitle className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: selectedComponent.color }}
                >
                  {selectedComponent.number}
                </div>
                <span className="text-heading text-xl">{selectedComponent.name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Use InteractiveColumns for normal mode too */}
              <InteractiveColumns
                component={selectedComponent}
                onSelectItem={handleSelectItem}
                isItemSelected={isItemSelected}
                compact={false}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <ImageLibraryManager
        isOpen={isImageLibraryOpen}
        onClose={() => setIsImageLibraryOpen(false)}
        assetType="transformers"
        componentLabels={components.map(c => c.name)}
        onImageSaved={handleImageSaved}
      />

      <AssetConfigManager
        isOpen={isConfigOpen}
        onClose={() => setIsConfigOpen(false)}
        assetType="transformers"
        onConfigSaved={handleConfigSaved}
      />
    </div>
  );
};

export default TransformerPage;
